import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { LineChart as LC, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, Area, AreaChart } from "recharts";
import { LineChart, TrendingUp, Music, Youtube, Link2, Unlink, Loader2, ShieldCheck } from "lucide-react";

const fmtCompact = (n) => {
  if (n == null) return "—";
  if (n >= 1e9) return (n / 1e9).toFixed(2) + "B";
  if (n >= 1e6) return (n / 1e6).toFixed(2) + "M";
  if (n >= 1e3) return (n / 1e3).toFixed(1) + "K";
  return String(n);
};

export default function Analytics() {
  const [data, setData] = useState(null);
  const [spotify, setSpotify] = useState(null);
  const [youtube, setYoutube] = useState(null);
  const [google, setGoogle] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [ytHandle, setYtHandle] = useState("");
  const [ytConnecting, setYtConnecting] = useState(false);
  const [googleConnecting, setGoogleConnecting] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const loadAll = () => {
    Promise.all([
      api.get("/analytics/summary"),
      api.get("/spotify/status"),
      api.get("/youtube/status"),
      api.get("/google/status"),
    ]).then(([a, s, y, g]) => {
      setData(a.data);
      setSpotify(s.data);
      setYoutube(y.data);
      setGoogle(g.data);
    }).catch(() => {});
  };

  useEffect(() => { loadAll(); }, []);

  useEffect(() => {
    const s = searchParams.get("spotify");
    const g = searchParams.get("google");
    if (s === "connected") toast.success("Spotify connected");
    else if (s === "denied") toast.error("Spotify connection denied");
    else if (s === "state_expired") toast.error("Session expired, please retry");
    else if (s === "exchange_failed") toast.error("Spotify token exchange failed");
    else if (s === "error") toast.error("Spotify OAuth error");
    if (g === "connected") toast.success("Google connected — YouTube Analytics unlocked");
    else if (g === "denied") toast.error("Google connection denied");
    else if (g === "state_expired") toast.error("Google session expired, please retry");
    else if (g === "exchange_failed") toast.error("Google token exchange failed");
    else if (g === "error") toast.error("Google OAuth error");
    if (s || g) {
      searchParams.delete("spotify");
      searchParams.delete("google");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const connectSpotify = async () => {
    setConnecting(true);
    try {
      const { data } = await api.get("/spotify/login");
      window.location.href = data.auth_url;
    } catch (e) {
      toast.error("Could not start Spotify OAuth");
      setConnecting(false);
    }
  };
  const disconnectSpotify = async () => {
    if (!confirm("Disconnect Spotify?")) return;
    await api.post("/spotify/disconnect");
    toast.success("Spotify disconnected");
    loadAll();
  };

  const connectGoogle = async () => {
    setGoogleConnecting(true);
    try {
      const { data } = await api.get("/google/login");
      window.location.href = data.auth_url;
    } catch (e) {
      toast.error("Could not start Google OAuth");
      setGoogleConnecting(false);
    }
  };
  const disconnectGoogle = async () => {
    if (!confirm("Disconnect Google?")) return;
    await api.post("/google/disconnect");
    toast.success("Google disconnected");
    loadAll();
  };

  const connectYouTube = async (e) => {
    e?.preventDefault();
    if (!ytHandle.trim()) return toast.error("Enter a YouTube handle or URL");
    setYtConnecting(true);
    try {
      await api.post("/youtube/connect", { handle: ytHandle.trim() });
      toast.success("YouTube channel connected");
      setYtHandle("");
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not find channel");
    } finally { setYtConnecting(false); }
  };
  const disconnectYouTube = async () => {
    if (!confirm("Disconnect YouTube channel?")) return;
    await api.post("/youtube/disconnect");
    toast.success("YouTube disconnected");
    loadAll();
  };

  if (!data) return <div className="p-10 text-zinc-500 sd-label">Loading analytics…</div>;

  const series = data.series.labels.map((l, i) => ({
    day: l.slice(5),
    yt_views: data.series.youtube_views[i],
    yt_subs: data.series.youtube_subs[i],
    sp_streams: data.series.spotify_streams[i],
    sp_followers: data.series.spotify_followers[i],
  }));

  const real = data.spotify_real;
  const ytReal = data.youtube_real;

  return (
    <div className="min-h-screen">
      <div className="sticky top-0 z-30 bg-[#0a0a0a]/80 backdrop-blur-xl border-b border-[#27272a]">
        <div className="px-4 md:px-8 min-h-16 flex flex-wrap items-center gap-2 md:gap-3 py-3 md:py-0">
          <LineChart size={16} strokeWidth={1.5} className="text-[#FF3B30]" />
          <div className="font-display text-xl">Analytics</div>
          <div className="sd-label ml-2">LAST 30 DAYS</div>
          <div className="ml-auto text-xs text-zinc-500 font-mono max-w-[520px] truncate">{data.note}</div>
        </div>
      </div>

      <div className="p-4 md:p-8 max-w-[1400px] mx-auto space-y-4">
        {/* Google upgrade banner — shown when connected to YouTube via API key only */}
        {!google?.connected && (
          <div className="sd-card p-5 flex flex-col md:flex-row md:items-center gap-4 border-[#FF3B30]/40" data-testid="google-connect-strip">
            <div className="flex items-center gap-4 md:flex-1 min-w-0">
              <div className="w-10 h-10 border border-[#27272a] flex items-center justify-center bg-[#141414] shrink-0">
                <ShieldCheck size={16} strokeWidth={1.5} className="text-[#FF3B30]" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="sd-label">UPGRADE · GOOGLE OAUTH</div>
                <div className="mt-1 text-sm text-zinc-300">
                  Connect Google to unlock <span className="text-white">real CTR, watch time, and 30-day view timeseries</span> for your channel.
                </div>
              </div>
            </div>
            <button onClick={connectGoogle} disabled={googleConnecting} className="sd-btn h-9 shrink-0 w-full md:w-auto" style={{ background: "#4285F4", color: "#ffffff" }} data-testid="google-connect-btn">
              {googleConnecting ? <Loader2 size={13} className="animate-spin" /> : <Link2 size={13} strokeWidth={1.5} />}
              Connect Google
            </button>
          </div>
        )}
        {google?.connected && (
          <div className="sd-card p-5 flex flex-col md:flex-row md:items-center gap-4" data-testid="google-connect-strip">
            <div className="flex items-center gap-4 md:flex-1 min-w-0">
              <div className="w-10 h-10 border border-[#27272a] flex items-center justify-center shrink-0" style={{ background: "#34C75922" }}>
                <ShieldCheck size={16} strokeWidth={1.5} style={{ color: "#34C759" }} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="sd-label">GOOGLE · CONNECTED</div>
                <div className="mt-1 text-sm text-zinc-300">
                  {data?.youtube_analytics_live
                    ? "Live YouTube Analytics active. CTR, watch time, and daily views are real."
                    : "Connected. Publish some videos to see analytics data populate."}
                </div>
              </div>
            </div>
            <button onClick={disconnectGoogle} className="sd-btn sd-btn-ghost h-9 shrink-0 w-full md:w-auto" data-testid="google-disconnect-btn">
              <Unlink size={13} strokeWidth={1.5} /> Disconnect
            </button>
          </div>
        )}

        {/* Connection strips */}
        <div className="grid md:grid-cols-2 gap-4">
          {/* YouTube */}
          <div className="sd-card p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="youtube-connect-strip">
            <div className="flex items-center gap-4 sm:flex-1 min-w-0">
              <div className="w-10 h-10 border border-[#27272a] flex items-center justify-center shrink-0" style={{ background: youtube?.connected ? "#FF000022" : "#141414" }}>
                <Youtube size={16} strokeWidth={1.5} style={{ color: youtube?.connected ? "#FF0000" : "#a1a1aa" }} />
              </div>
              <div className="min-w-0 flex-1">
                {youtube?.connected ? (
                  <>
                    <div className="sd-label">YOUTUBE · CONNECTED</div>
                    <div className="mt-1 text-sm truncate">
                      <span className="text-white">{youtube.channel?.title}</span>
                      <span className="text-zinc-500 font-mono"> · {fmtCompact(youtube.channel?.subscribers)} subs</span>
                    </div>
                  </>
                ) : (
                  <div className="sd-label">YOUTUBE · PUBLIC DATA</div>
                )}
              </div>
            </div>
            {!youtube?.connected ? (
              <form onSubmit={connectYouTube} className="flex items-center gap-2 w-full sm:w-auto">
                <input
                  value={ytHandle}
                  onChange={(e) => setYtHandle(e.target.value)}
                  placeholder="@yourhandle"
                  className="sd-input h-9 flex-1 sm:w-[180px]"
                  disabled={ytConnecting}
                  data-testid="youtube-handle-input"
                />
                <button type="submit" disabled={ytConnecting} className="sd-btn h-9 shrink-0" style={{ background: "#FF0000", color: "#ffffff" }} data-testid="youtube-connect-btn">
                  {ytConnecting ? <Loader2 size={13} className="animate-spin" /> : <Link2 size={13} strokeWidth={1.5} />}
                  Connect
                </button>
              </form>
            ) : (
              <button onClick={disconnectYouTube} className="sd-btn sd-btn-ghost h-9 shrink-0 w-full sm:w-auto" data-testid="youtube-disconnect-btn">
                <Unlink size={13} strokeWidth={1.5} /> Disconnect
              </button>
            )}
          </div>

          {/* Spotify */}
          <div className="sd-card p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="spotify-connect-strip">
            <div className="flex items-center gap-4 sm:flex-1 min-w-0">
              <div className="w-10 h-10 border border-[#27272a] flex items-center justify-center shrink-0" style={{ background: spotify?.connected ? "#1DB95422" : "#141414" }}>
                <Music size={16} strokeWidth={1.5} style={{ color: spotify?.connected ? "#1DB954" : "#1DB954" }} />
              </div>
              <div className="min-w-0 flex-1">
                {spotify?.connected ? (
                  <>
                    <div className="sd-label">SPOTIFY · CONNECTED</div>
                    <div className="mt-1 text-sm truncate">
                      <span className="text-white">{spotify.profile?.display_name || "Your account"}</span>
                      {spotify.profile?.product && <span className="text-zinc-500 font-mono"> · {spotify.profile.product}</span>}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="sd-label">SPOTIFY · NOT CONNECTED</div>
                    <div className="mt-1 text-xs text-zinc-500 leading-relaxed">
                      Pull your top tracks, artists, and podcast listening data alongside YouTube.
                    </div>
                  </>
                )}
              </div>
            </div>
            {spotify?.connected ? (
              <button onClick={disconnectSpotify} className="sd-btn sd-btn-ghost h-9 shrink-0 w-full sm:w-auto" data-testid="spotify-disconnect-btn">
                <Unlink size={13} strokeWidth={1.5} /> Disconnect
              </button>
            ) : (
              <button
                onClick={connectSpotify}
                className="sd-btn h-9 shrink-0 w-full sm:w-auto"
                style={{ background: "#1DB954", color: "#0a0a0a" }}
                data-testid="spotify-connect-btn"
              >
                <Link2 size={13} strokeWidth={1.5} />
                Connect Spotify
              </button>
            )}
          </div>
        </div>

        {/* Real YouTube block */}
        {ytReal && (
          <div className="grid md:grid-cols-3 gap-4">
            <div className="sd-card p-6 md:col-span-1">
              <div className="sd-label">YOUR CHANNEL · LIVE</div>
              <div className="flex items-center gap-3 mt-3">
                {ytReal.channel?.thumbnail ? (
                  <img src={ytReal.channel.thumbnail} alt="" className="w-14 h-14 border border-[#27272a] object-cover" />
                ) : (
                  <div className="w-14 h-14 border border-[#27272a] bg-[#141414] flex items-center justify-center">
                    <Youtube size={18} strokeWidth={1.5} className="text-zinc-500" />
                  </div>
                )}
                <div className="min-w-0">
                  <div className="font-display text-2xl truncate">{ytReal.channel?.title}</div>
                  <div className="sd-label mt-1 truncate">{ytReal.channel?.handle}</div>
                </div>
              </div>
              <div className="mt-5 grid grid-cols-3 gap-2">
                <div className="border border-[#27272a] p-3">
                  <div className="sd-label">SUBS</div>
                  <div className="font-mono text-lg mt-1">{fmtCompact(ytReal.channel?.subscribers)}</div>
                </div>
                <div className="border border-[#27272a] p-3">
                  <div className="sd-label">VIEWS</div>
                  <div className="font-mono text-lg mt-1">{fmtCompact(ytReal.channel?.views_total)}</div>
                </div>
                <div className="border border-[#27272a] p-3">
                  <div className="sd-label">VIDEOS</div>
                  <div className="font-mono text-lg mt-1">{ytReal.channel?.video_count?.toLocaleString() || 0}</div>
                </div>
              </div>
              {ytReal.channel?.description && (
                <p className="mt-4 text-xs text-zinc-500 leading-relaxed line-clamp-4">{ytReal.channel.description}</p>
              )}
            </div>

            <div className="sd-card p-6 md:col-span-2">
              <div className="sd-label mb-4">RECENT UPLOADS</div>
              {ytReal.recent_videos?.length ? (
                <div className="space-y-2">
                  {ytReal.recent_videos.map((v) => (
                    <a
                      key={v.id}
                      href={`https://www.youtube.com/watch?v=${v.id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="grid grid-cols-[80px_1fr_100px_80px] items-center gap-3 py-2 border-b border-[#27272a] last:border-0 hover:bg-[#141414] transition-colors px-2 -mx-2"
                      data-testid={`yt-video-${v.id}`}
                    >
                      {v.thumbnail ? (
                        <img src={v.thumbnail} alt="" className="w-20 h-11 object-cover" />
                      ) : (
                        <div className="w-20 h-11 bg-[#141414]" />
                      )}
                      <div className="min-w-0">
                        <div className="text-sm text-white truncate">{v.title}</div>
                        <div className="text-xs text-zinc-500 font-mono">{new Date(v.published_at).toLocaleDateString()}</div>
                      </div>
                      <div className="font-mono text-sm text-right">{fmtCompact(v.views)}</div>
                      <div className="font-mono text-xs text-zinc-500 text-right">{v.likes != null ? fmtCompact(v.likes) + " ♡" : "—"}</div>
                    </a>
                  ))}
                </div>
              ) : (
                <div className="text-sm text-zinc-500">No recent videos found.</div>
              )}
            </div>
          </div>
        )}

        {/* Real Spotify block */}
        {real && (
          <div className="grid md:grid-cols-3 gap-4">
            <div className="sd-card p-6 md:col-span-1">
              <div className="sd-label">YOUR LISTENING · LIVE</div>
              <div className="flex items-center gap-3 mt-3">
                {real.profile?.images?.[0]?.url ? (
                  <img src={real.profile.images[0].url} alt="" className="w-14 h-14 border border-[#27272a] object-cover" />
                ) : (
                  <div className="w-14 h-14 border border-[#27272a] bg-[#141414] flex items-center justify-center">
                    <Music size={18} strokeWidth={1.5} className="text-zinc-500" />
                  </div>
                )}
                <div className="min-w-0">
                  <div className="font-display text-2xl truncate">{real.profile?.display_name || "—"}</div>
                  <div className="sd-label mt-1">{real.profile?.country || "—"} · {real.profile?.product || "—"}</div>
                </div>
              </div>
              <div className="mt-5 grid grid-cols-2 gap-2">
                <div className="border border-[#27272a] p-3">
                  <div className="sd-label">FOLLOWERS</div>
                  <div className="font-mono text-lg mt-1">{real.profile?.followers ?? 0}</div>
                </div>
                <div className="border border-[#27272a] p-3">
                  <div className="sd-label">FOLLOWING</div>
                  <div className="font-mono text-lg mt-1">{real.following_artists ?? 0}</div>
                </div>
                <div className="border border-[#27272a] p-3 col-span-2">
                  <div className="sd-label">RECENTLY PLAYED</div>
                  <div className="font-mono text-lg mt-1">{real.recent_count} tracks</div>
                </div>
              </div>
            </div>

            <div className="sd-card p-6 md:col-span-2">
              <div className="sd-label mb-4">YOUR TOP TRACKS · LAST 4 WEEKS</div>
              {real.top_tracks?.length ? (
                <ol className="space-y-2">
                  {real.top_tracks.map((t, i) => (
                    <li key={i} className="grid grid-cols-[24px_40px_1fr_60px] items-center gap-3 py-2 border-b border-[#27272a] last:border-0">
                      <div className="font-mono text-zinc-500 text-xs">{String(i + 1).padStart(2, "0")}</div>
                      {t.album_image ? (
                        <img src={t.album_image} alt="" className="w-10 h-10 object-cover" />
                      ) : (
                        <div className="w-10 h-10 bg-[#141414]" />
                      )}
                      <div className="min-w-0">
                        <div className="text-white text-sm truncate">{t.name}</div>
                        <div className="text-xs text-zinc-500 truncate">{t.artist}</div>
                      </div>
                      <div className="font-mono text-xs text-[#FF3B30] text-right">pop {t.popularity}</div>
                    </li>
                  ))}
                </ol>
              ) : (
                <div className="text-sm text-zinc-500">No top tracks yet — go stream something!</div>
              )}
            </div>

            {real.top_artists?.length > 0 && (
              <div className="sd-card p-6 md:col-span-3">
                <div className="sd-label mb-4">YOUR TOP ARTISTS</div>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {real.top_artists.map((a, i) => (
                    <div key={i} className="border border-[#27272a] p-3 hover:border-zinc-500 transition-colors">
                      {a.image ? (
                        <img src={a.image} alt="" className="w-full aspect-square object-cover mb-2" />
                      ) : (
                        <div className="w-full aspect-square bg-[#141414] mb-2" />
                      )}
                      <div className="text-sm text-white truncate">{a.name}</div>
                      <div className="sd-label mt-1 truncate">{a.genres?.[0] || "—"}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {real.saved_shows?.length > 0 && (
              <div className="sd-card p-6 md:col-span-3">
                <div className="sd-label mb-4">SAVED PODCASTS</div>
                <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
                  {real.saved_shows.map((s, i) => (
                    <div key={i} className="border border-[#27272a] p-3 hover:border-zinc-500 transition-colors">
                      {s.image ? (
                        <img src={s.image} alt="" className="w-full aspect-square object-cover mb-2" />
                      ) : (
                        <div className="w-full aspect-square bg-[#141414] mb-2" />
                      )}
                      <div className="text-xs text-white truncate">{s.name}</div>
                      <div className="sd-label mt-1 truncate">{s.publisher}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="sd-label mt-8 mb-2 px-1">
          {data.youtube_analytics_live
            ? "TIMESERIES · LIVE FROM YOUTUBE ANALYTICS"
            : ytReal
              ? "TIMESERIES · DEMO DATA (CONNECT GOOGLE FOR LIVE)"
              : "CHANNEL METRICS · DEMO DATA"}
        </div>

        {/* Bento KPI grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="sd-card p-6 md:col-span-2">
            <div className="flex items-baseline justify-between">
              <div className="sd-label">YOUTUBE // 30D VIEWS</div>
              <div className="font-mono text-[#34C759] text-xs">+18.4%</div>
            </div>
            <div className="font-mono text-5xl mt-3">{data.kpis.youtube.views_30d.toLocaleString()}</div>
            <div className="mt-4 h-24">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={series}>
                  <defs>
                    <linearGradient id="gViews" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#FF3B30" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#FF3B30" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <Area dataKey="yt_views" stroke="#FF3B30" strokeWidth={1.5} fill="url(#gViews)" />
                  <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid #27272a", fontFamily: "JetBrains Mono", fontSize: 11 }} labelStyle={{ color: "#a1a1aa" }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="sd-card p-6">
            <div className="sd-label">SUBSCRIBERS</div>
            <div className="font-mono text-4xl mt-3">{data.kpis.youtube.subscribers.toLocaleString()}</div>
            <div className="text-xs text-zinc-500 mt-1">total</div>
            <div className="mt-4 h-12">
              <ResponsiveContainer width="100%" height="100%">
                <LC data={series}><Line dataKey="yt_subs" stroke="#FFFFFF" strokeWidth={1.25} dot={false} /></LC>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="sd-card p-6">
            <div className="flex items-center justify-between">
              <div className="sd-label">CTR</div>
              {data.kpis.youtube.live && <span className="sd-label text-[#34C759]">● LIVE</span>}
            </div>
            <div className="font-mono text-4xl mt-3">{data.kpis.youtube.ctr}%</div>
            <div className="text-xs text-zinc-500 mt-1">click-through rate</div>
            <div className="mt-4 flex items-center gap-2 font-mono text-xs">
              <TrendingUp size={12} strokeWidth={1.5} className="text-[#34C759]" />
              <span className="text-[#34C759]">+0.42pt</span>
            </div>
          </div>

          <div className="sd-card p-6">
            <div className="flex items-center justify-between">
              <div className="sd-label">WATCH HOURS</div>
              {data.kpis.youtube.live && <span className="sd-label text-[#34C759]">● LIVE</span>}
            </div>
            <div className="font-mono text-4xl mt-3">{data.kpis.youtube.watch_hours_30d.toLocaleString()}</div>
            <div className="text-xs text-zinc-500 mt-1">hours · 30d</div>
          </div>

          <div className="sd-card p-6">
            <div className="flex items-center justify-between">
              <div className="sd-label">AVG. VIEW DURATION</div>
              {data.kpis.youtube.live && <span className="sd-label text-[#34C759]">● LIVE</span>}
            </div>
            <div className="font-mono text-4xl mt-3">{data.kpis.youtube.avg_view_duration}</div>
            <div className="text-xs text-zinc-500 mt-1">per session</div>
          </div>

          <div className="sd-card p-6 md:col-span-2">
            <div className="flex items-baseline justify-between">
              <div className="sd-label">SPOTIFY // 30D STREAMS {real ? "· demo podcast data" : ""}</div>
              <div className="font-mono text-[#34C759] text-xs">+22.1%</div>
            </div>
            <div className="font-mono text-5xl mt-3">{data.kpis.spotify.streams_30d.toLocaleString()}</div>
            <div className="mt-4 h-24">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={series}>
                  <defs>
                    <linearGradient id="gStreams" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#FFFFFF" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="#FFFFFF" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <Area dataKey="sp_streams" stroke="#FFFFFF" strokeWidth={1.5} fill="url(#gStreams)" />
                  <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid #27272a", fontFamily: "JetBrains Mono", fontSize: 11 }} labelStyle={{ color: "#a1a1aa" }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="sd-card p-6">
            <div className="sd-label">SPOTIFY FOLLOWERS</div>
            <div className="font-mono text-4xl mt-3">{data.kpis.spotify.followers.toLocaleString()}</div>
            <div className="text-xs text-zinc-500 mt-1">total</div>
          </div>

          <div className="sd-card p-6">
            <div className="sd-label">AVG LISTEN %</div>
            <div className="font-mono text-4xl mt-3">{data.kpis.spotify.avg_listen_pct}%</div>
            <div className="text-xs text-zinc-500 mt-1">per stream</div>
          </div>
        </div>

        {/* Chart row: views vs streams */}
        <div className="sd-card p-6">
          <div className="flex items-baseline justify-between mb-6">
            <div>
              <div className="sd-label">CROSS-PLATFORM // 30 DAYS</div>
              <div className="font-display text-2xl mt-2">Views (YouTube) vs Streams (Spotify)</div>
            </div>
            <div className="flex items-center gap-4 text-xs font-mono text-zinc-500">
              <span className="flex items-center gap-2"><span className="inline-block w-3 h-[2px] bg-[#FF3B30]" /> YT VIEWS</span>
              <span className="flex items-center gap-2"><span className="inline-block w-3 h-[2px] bg-white" /> SP STREAMS</span>
            </div>
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LC data={series} margin={{ left: 0, right: 20, top: 10, bottom: 0 }}>
                <XAxis dataKey="day" tick={{ fill: "#52525b", fontFamily: "JetBrains Mono", fontSize: 10 }} tickLine={false} axisLine={{ stroke: "#27272a" }} />
                <YAxis tick={{ fill: "#52525b", fontFamily: "JetBrains Mono", fontSize: 10 }} tickLine={false} axisLine={{ stroke: "#27272a" }} />
                <Tooltip contentStyle={{ background: "#0a0a0a", border: "1px solid #27272a", fontFamily: "JetBrains Mono", fontSize: 11 }} labelStyle={{ color: "#a1a1aa" }} />
                <Line type="monotone" dataKey="yt_views" stroke="#FF3B30" strokeWidth={1.5} dot={false} />
                <Line type="monotone" dataKey="sp_streams" stroke="#FFFFFF" strokeWidth={1.5} dot={false} />
              </LC>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top content */}
        <div className="sd-card p-6">
          <div className="sd-label mb-4 flex items-center justify-between">
            <span>TOP CONTENT · LAST 7 DAYS</span>
            {data.top_content?.[0]?.live && (
              <span className="text-[#34C759] font-mono text-[10px]">● LIVE</span>
            )}
            {data.top_content?.[0]?.live === false && (
              <span className="text-zinc-600 font-mono text-[10px]">● DEMO — CONNECT YOUTUBE / SPOTIFY</span>
            )}
          </div>
          <div>
            {data.top_content.map((c, i) => {
              const RowContent = (
                <>
                  <div className="font-mono text-zinc-500 text-xs">{String(i + 1).padStart(2, "0")}</div>
                  {c.thumbnail ? (
                    <img
                      src={c.thumbnail}
                      alt=""
                      className="w-14 h-14 object-cover border border-[#27272a]"
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    <div className="w-14 h-14 border border-[#27272a] bg-[#141414] flex items-center justify-center">
                      {c.platform === "youtube" ? (
                        <Youtube size={14} strokeWidth={1.5} className="text-[#FF3B30]" />
                      ) : (
                        <Music size={14} strokeWidth={1.5} className="text-[#1DB954]" />
                      )}
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="text-white truncate">{c.title}</div>
                    <div className="sd-label mt-1">
                      {c.platform}
                      {c.live && <span className="text-[#34C759] ml-2">● live</span>}
                    </div>
                  </div>
                  <div className="font-mono text-sm text-right whitespace-nowrap">{c.metric}</div>
                  <div className="font-mono text-sm text-[#34C759] text-right whitespace-nowrap">{c.delta}</div>
                </>
              );
              const rowCls = "grid grid-cols-[40px_56px_1fr_140px_80px] gap-3 items-center py-4 border-t border-[#27272a]";
              return c.url ? (
                <a
                  key={i}
                  href={c.url}
                  target="_blank"
                  rel="noreferrer"
                  className={`${rowCls} hover:bg-[#141414] transition-colors -mx-2 px-2`}
                  data-testid={`top-content-row-${i}`}
                >
                  {RowContent}
                </a>
              ) : (
                <div key={i} className={rowCls} data-testid={`top-content-row-${i}`}>
                  {RowContent}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
